import { httpClient } from '@/services/http/http-client';
import type { Page, PageRequest } from '@/types/api.types';

import {
  type AttendanceSummary,
  attendanceSummarySchema,
  type CheckIns,
  checkInsSchema,
  type CommandProgress,
  commandProgressSchema,
  type DeviceStatus,
  deviceStatusSchema,
  type HourCount,
  hourCountSchema,
  type InactiveMember,
  inactiveMemberSchema,
  type MachineUser,
  machineUserSchema,
  type MemberAttendance,
  memberAttendanceSchema,
  type Punch,
  punchSchema,
} from '../schemas/attendance.schema';

/** The only place this feature talks to the network. */

export interface PunchListFilter extends PageRequest {
  /** Calendar days in the gym's time zone, both inclusive. */
  from?: string | undefined;
  to?: string | undefined;
}

export interface MachineUserFilter extends PageRequest {
  linked?: boolean | undefined;
  q?: string | undefined;
}

export type EnrolSlot = { method: 'face' } | { method: 'finger'; finger: number };

export function listPunches(filter: PunchListFilter, signal?: AbortSignal): Promise<Page<Punch>> {
  return httpClient.getPage('/attendance/punches', {
    schema: punchSchema,
    query: { ...filter },
    signal,
  });
}

/** Who came in on `date`, earliest first. */
export function getCheckIns(date: string, signal?: AbortSignal): Promise<CheckIns> {
  return httpClient.get('/attendance/check-ins', {
    schema: checkInsSchema,
    query: { date },
    ...(signal ? { signal } : {}),
  });
}

export function listDevices(signal?: AbortSignal): Promise<DeviceStatus[]> {
  return httpClient.get('/attendance/devices', {
    schema: deviceStatusSchema.array(),
    ...(signal ? { signal } : {}),
  });
}

export function getSummary(date: string, signal?: AbortSignal): Promise<AttendanceSummary> {
  return httpClient.get('/attendance/summary', {
    schema: attendanceSummarySchema,
    query: { date },
    ...(signal ? { signal } : {}),
  });
}

export function listInactive(
  days: number,
  page: PageRequest,
  signal?: AbortSignal,
): Promise<Page<InactiveMember>> {
  return httpClient.getPage('/attendance/inactive', {
    schema: inactiveMemberSchema,
    query: { days, ...page },
    signal,
  });
}

export function getPeakHours(from: string, to: string, signal?: AbortSignal): Promise<HourCount[]> {
  return httpClient.get('/attendance/peak-hours', {
    schema: hourCountSchema.array(),
    query: { from, to },
    ...(signal ? { signal } : {}),
  });
}

export function listMachineUsers(
  filter: MachineUserFilter,
  signal?: AbortSignal,
): Promise<Page<MachineUser>> {
  return httpClient.getPage('/attendance/users', {
    schema: machineUserSchema,
    query: { ...filter },
    signal,
  });
}

/** Links a machine user to a member; `null` unlinks. */
export function linkMachineUser(userId: number, memberId: number | null): Promise<MachineUser> {
  return httpClient.patch(
    `/attendance/users/${String(userId)}/member`,
    { memberId },
    { schema: machineUserSchema },
  );
}

export function getMemberAttendance(
  memberId: number,
  signal?: AbortSignal,
): Promise<MemberAttendance> {
  return httpClient.get(`/attendance/members/${String(memberId)}`, {
    schema: memberAttendanceSchema,
    ...(signal ? { signal } : {}),
  });
}

export function enrolMember(memberId: number, slot: EnrolSlot): Promise<CommandProgress> {
  return httpClient.post(`/attendance/members/${String(memberId)}/enrol`, slot, {
    schema: commandProgressSchema,
  });
}

export function restartDevice(deviceId: number): Promise<CommandProgress> {
  return httpClient.post(`/attendance/devices/${String(deviceId)}/restart`, undefined, {
    schema: commandProgressSchema,
  });
}

export function getCommand(id: number, signal?: AbortSignal): Promise<CommandProgress> {
  return httpClient.get(`/attendance/commands/${String(id)}`, {
    schema: commandProgressSchema,
    ...(signal ? { signal } : {}),
  });
}
